import type { BuildContext, Fragment } from "../types.js";
import type { SceneInputs } from "../../scenes/types.js";
import { escapeHtml } from "../composition.js";
import { colors } from "../../brand/theme.js";

/**
 * Args passed to every scene-builder. `clipDurationSec` is the duration of the
 * Reel timeline slot the scene occupies (i.e. clip length, possibly trimmed
 * or padded relative to the scene's natural pacing). `clipPrefix` is a
 * stable per-clip id prefix so multiple instances of the same scene can
 * coexist on one composition.
 */
export type SceneBuildArgs = {
  ctx: BuildContext;
  clipPrefix: string;
  inputs: SceneInputs;
  clipDurationSec: number;
  naturalDurationSec?: number;
  playbackRate?: number;
  /** Absolute composition start time of the clip — for tweens that need to
   *  be aligned with the project-wide timeline. The scene builder can choose
   *  to ignore this and emit tweens at clip-local time (0 = clip start). */
  clipStartSec: number;
};

export type SceneBuilder = (args: SceneBuildArgs) => Fragment;

/**
 * Placeholder builder — renders a labeled rectangle with the scene id so a
 * Reel containing un-ported scenes still produces a watchable output. Once
 * Phase 3b/c/d finishes, this is only used as a fallback for unknown scene
 * ids (typo / future scenes).
 */
function placeholderBuilder(label: string): SceneBuilder {
  return ({ clipPrefix, inputs }) => {
    const id = `${clipPrefix}-ph`;
    const accent =
      inputs.accent === "secondary"
        ? colors.secondary
        : inputs.accent === "accent"
          ? colors.accent
          : colors.primary;
    const text = inputs.text ?? label;
    const html = `<div id="${id}" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;background:linear-gradient(135deg, ${accent}33 0%, ${colors.bg} 70%);color:${colors.fg};text-align:center;padding:48px;font-family:'Outfit',sans-serif;">
  <div style="font-size:24px;letter-spacing:6px;text-transform:uppercase;color:${accent};font-weight:700;margin-bottom:18px;">${escapeHtml(label)}</div>
  <div style="font-size:64px;font-weight:900;line-height:1.05;text-wrap:balance;max-width:80%;">${escapeHtml(text)}</div>
  ${inputs.subtext ? `<div style="font-size:36px;color:${colors.muted};margin-top:24px;text-wrap:balance;max-width:75%;">${escapeHtml(inputs.subtext)}</div>` : ""}
</div>`;
    return { html };
  };
}

/**
 * Scene-id → builder registry. Each scene we port adds an entry here. Until
 * all ~80 scenes are ported (phase 3b/c/d) we fall back to a labeled
 * placeholder via {@link getSceneBuilder}.
 */
// Bespoke ports (visual parity with the Remotion original):
import { kineticHook } from "../scenes/text/kineticHook.js";
import { bigNumber } from "../scenes/data/bigNumber.js";
import { bulletStack } from "../scenes/list/bulletStack.js";
import { logoOutro } from "../scenes/outro/logoOutro.js";
import { brandFlash } from "../scenes/intro/brandFlash.js";
import { flashStop } from "../scenes/text/flashStop.js";
import { gradientReveal } from "../scenes/text/gradientReveal.js";
import { splitHero } from "../scenes/text/splitHero.js";
import { quoteSlam } from "../scenes/text/quoteSlam.js";
import { highlightSweep } from "../scenes/text/highlightSweep.js";
import { typewriter } from "../scenes/text/typewriter.js";
import { handwritten } from "../scenes/text/handwritten.js";
import { splitFrameStory } from "../scenes/text/splitFrameStory.js";
import { phoneMockup } from "../scenes/visual/phoneMockup.js";
import { browserMockup } from "../scenes/visual/browserMockup.js";
import { laptopMockup } from "../scenes/visual/laptopMockup.js";
import { tabletMockup } from "../scenes/visual/tabletMockup.js";
import { watchMockup } from "../scenes/visual/watchMockup.js";
import { phoneTilt3D } from "../scenes/visual/phoneTilt3D.js";
import { phoneCarousel } from "../scenes/visual/phoneCarousel.js";
import { phoneSplitCompare } from "../scenes/visual/phoneSplitCompare.js";
import { appStoreShowcase } from "../scenes/visual/appStoreShowcase.js";
import { deviceTrio } from "../scenes/visual/deviceTrio.js";
import { productCardLift } from "../scenes/visual/productCardLift.js";
import { chatBubbleStack } from "../scenes/visual/chatBubbleStack.js";
import { categoryDock } from "../scenes/visual/categoryDock.js";
import { categoryGrid } from "../scenes/visual/categoryGrid.js";
import { gridReveal } from "../scenes/visual/gridReveal.js";
import { featureMatrix } from "../scenes/visual/featureMatrix.js";
import { timelineFlow } from "../scenes/visual/timelineFlow.js";
import { processArrow } from "../scenes/visual/processArrow.js";
import { fourStepLaunch } from "../scenes/visual/fourStepLaunch.js";
import { trustSealStrip } from "../scenes/visual/trustSealStrip.js";
import { parallaxLayers } from "../scenes/visual/parallaxLayers.js";
import { colorBlockReveal } from "../scenes/visual/colorBlockReveal.js";
import { cardDeckFan } from "../scenes/visual/cardDeckFan.js";
import { marqueeStack } from "../scenes/visual/marqueeStack.js";
import { floatingLabels } from "../scenes/visual/floatingLabels.js";
import { tickerStrip } from "../scenes/visual/tickerStrip.js";
import { stickerSlam } from "../scenes/visual/stickerSlam.js";
import { marketplaceMap } from "../scenes/visual/marketplaceMap.js";
import { mallDirectory } from "../scenes/visual/mallDirectory.js";
import { bentoShowcase } from "../scenes/visual/bentoShowcase.js";
import { pillMarquee } from "../scenes/visual/pillMarquee.js";
import { notificationStack } from "../scenes/visual/notificationStack.js";
import { localRadarSweep } from "../scenes/visual/localRadarSweep.js";
import { searchTypeResults } from "../scenes/visual/searchTypeResults.js";
import { categoryOrbit } from "../scenes/visual/categoryOrbit.js";
import { appFlowWalkthrough } from "../scenes/visual/appFlowWalkthrough.js";
import { screenScrollPan } from "../scenes/visual/screenScrollPan.js";
import { featureSpotlight } from "../scenes/visual/featureSpotlight.js";
import { kpiCountUp } from "../scenes/data/kpiCountUp.js";
import { testimonialGlow } from "../scenes/text/testimonialGlow.js";
import { numberedSteps } from "../scenes/list/numberedSteps.js";
import { checklistReveal } from "../scenes/list/checklistReveal.js";
import { featureRows } from "../scenes/list/featureRows.js";
import { textBuildUp } from "../scenes/text/textBuildUp.js";
import { definitionCard } from "../scenes/text/definitionCard.js";
import { swipeRevealIntro } from "../scenes/intro/swipeRevealIntro.js";
import { ctaPulseOutro } from "../scenes/outro/ctaPulseOutro.js";
import { commissionFlow } from "../scenes/visual/commissionFlow.js";
import { beforeAfterSlider } from "../scenes/visual/beforeAfterSlider.js";
import { comparisonSplit } from "../scenes/visual/comparisonSplit.js";
import { cityPin } from "../scenes/visual/cityPin.js";
import { iconLine } from "../scenes/visual/iconLine.js";
import { geometricBurst } from "../scenes/visual/geometricBurst.js";
import { noiseField } from "../scenes/visual/noiseField.js";
import { lottiePlay } from "../scenes/visual/lottiePlay.js";
import { motionBlurReveal } from "../scenes/text/motionBlurReveal.js";
import { pathDraw } from "../scenes/intro/pathDraw.js";
import { logoBeamStreak } from "../scenes/intro/logoBeamStreak.js";
import { logoStamp } from "../scenes/intro/logoStamp.js";
import { logoCircleSweep } from "../scenes/intro/logoCircleSweep.js";
import { cinematicHero } from "../scenes/intro/cinematicHero.js";
import { cinematicReveal } from "../scenes/intro/cinematicReveal.js";
import { apertureOpen } from "../scenes/intro/apertureOpen.js";
import { countdownIntro } from "../scenes/intro/countdownIntro.js";
import { glitchIntro } from "../scenes/intro/glitchIntro.js";
import { letterboxIntro } from "../scenes/intro/letterboxIntro.js";
import { logoPrismSplit } from "../scenes/intro/logoPrismSplit.js";
import { logoRingBurst } from "../scenes/intro/logoRingBurst.js";
import { logoLiquidReveal } from "../scenes/intro/logoLiquidReveal.js";
import { logoOrbitLaunch } from "../scenes/intro/logoOrbitLaunch.js";
import { digitalEgg } from "../scenes/intro/digitalEgg.js";
import { heroOrbit } from "../scenes/intro/heroOrbit.js";
import { gradientHero } from "../scenes/intro/gradientHero.js";
import { partnerCardFlip } from "../scenes/partner/partnerCardFlip.js";
import { partnerComingSoon } from "../scenes/partner/partnerComingSoon.js";
import { partnerReveal } from "../scenes/partner/partnerReveal.js";
import { partnerOfferReveal } from "../scenes/partner/partnerOfferReveal.js";
import { partnerHeroReveal } from "../scenes/partner/partnerHeroReveal.js";
import { partnerServiceList } from "../scenes/partner/partnerServiceList.js";
import { partnerServiceDetail } from "../scenes/partner/partnerServiceDetail.js";
import { partnerServiceTeaser } from "../scenes/partner/partnerServiceTeaser.js";
import { partnerCategoryLineup } from "../scenes/partner/partnerCategoryLineup.js";
import { signOff } from "../scenes/outro/signOff.js";
import { partnerOutroMore } from "../scenes/outro/partnerOutroMore.js";

// Generic builders (cover the remaining ~100 scenes with brand-appropriate
// title/subtext/list/number layouts and shared motion presets). Each entry
// picks the closest archetype from `_generic.ts`. This keeps every scene
// renderable even before bespoke ports land; bespoke versions can be
// dropped in later by changing the import without touching call sites.
import {
  genericTitle, genericHero, genericList, genericNumber, genericPartner,
  genericCTA, genericData, genericOutro,
} from "../scenes/_generic.js";

export const SCENE_BUILDERS: Record<string, SceneBuilder> = {
  // ---- Bespoke ports
  kineticHook,
  bigNumber,
  bulletStack,
  logoOutro,
  brandFlash,
  flashStop,
  gradientReveal,
  splitHero,
  quoteSlam,
  highlightSweep,
  typewriter,
  handwritten,
  splitFrameStory,
  phoneMockup,
  browserMockup,
  laptopMockup,
  tabletMockup,
  watchMockup,
  phoneTilt3D,
  phoneCarousel,
  phoneSplitCompare,
  appStoreShowcase,
  deviceTrio,
  productCardLift,
  chatBubbleStack,
  categoryDock,
  categoryGrid,
  gridReveal,
  featureMatrix,
  timelineFlow,
  processArrow,
  fourStepLaunch,
  trustSealStrip,
  parallaxLayers,
  colorBlockReveal,
  cardDeckFan,
  marqueeStack,
  floatingLabels,
  tickerStrip,
  stickerSlam,
  marketplaceMap,
  mallDirectory,
  bentoShowcase,
  pillMarquee,
  notificationStack,
  localRadarSweep,
  searchTypeResults,
  categoryOrbit,
  appFlowWalkthrough,
  screenScrollPan,
  featureSpotlight,
  kpiCountUp,
  testimonialGlow,
  numberedSteps,
  checklistReveal,
  featureRows,
  textBuildUp,
  definitionCard,
  swipeRevealIntro,
  ctaPulseOutro,
  commissionFlow,
  beforeAfterSlider,
  comparisonSplit,
  cityPin,
  iconLine,
  geometricBurst,
  noiseField,
  lottiePlay,
  motionBlurReveal,
  pathDraw,
  logoBeamStreak,
  logoStamp,
  logoCircleSweep,
  cinematicHero,
  cinematicReveal,
  apertureOpen,
  countdownIntro,
  glitchIntro,
  letterboxIntro,
  logoPrismSplit,
  logoRingBurst,
  logoLiquidReveal,
  logoOrbitLaunch,
  digitalEgg,
  heroOrbit,
  gradientHero,
  partnerCardFlip,
  partnerComingSoon,
  partnerReveal,
  partnerOfferReveal,
  partnerHeroReveal,
  partnerServiceList,
  partnerServiceDetail,
  partnerServiceTeaser,
  partnerCategoryLineup,
  signOff,
  partnerOutroMore,

  // ---- Text
  wordMorph: genericHero,
  glassCard: genericTitle,
  bigEditorial: genericHero,
  splitDiagonal: genericHero,
  magazineCover: genericTitle,
  oversizedQuote: genericHero,
  swissPoster: genericHero,
  zeroRiskClaim: genericTitle,
  cancelAnytime: genericTitle,
  testimonialCard: genericTitle,

  // ---- Data
  pricingCard: genericData,
  statTrio: genericData,
  progressBar: genericData,
  priceCompare: genericData,
  ratingStars: genericData,
  countdownPulse: genericNumber,
  numberOdometer: genericNumber,
  chartPop: genericData,
  metricsDashboard: genericData,
  revenueCompareBars: genericData,
  pricingDuo: genericData,
  weekMath: genericData,

  // ---- List
  stepFlow: genericList,
  iconStack: genericList,
  categoryCarousel: genericList,
  socialProofWall: genericList,
  swipeStack: genericList,
  merchantGrid: genericList,
  pillarCards: genericList,
  benefitChecklist: genericList,
  reasonCarousel: genericList,

  // ---- Visual / Mockup → all 36 scenes have bespoke premium builders now (see ../scenes/visual/*).
  //      phoneMockup + browserMockup are imported above with the bespoke block.

  // ---- Partner
  partnerSpotlight: genericPartner,
  partnerStatusBoard: genericPartner,
  partnerStampReveal: genericPartner,
  partnerMapPin: genericPartner,
  partnerHeroMarquee: genericPartner,
  partnerCountdownLaunch: genericPartner,
  partnerLiveShowcase: genericPartner,
  // (partnerComingSoon, partnerServiceList, partnerCategoryLineup are now bespoke — see imports above.)

  // ---- Outro / CTA
  ctaButton: genericCTA,
  qrOutro: genericOutro,
  qrCardFlip: genericOutro,
  buttonStack: genericCTA,
  socialProofOutro: genericOutro,
  lockupOutro: genericOutro,
};

export function getSceneBuilder(sceneId: string): SceneBuilder {
  return SCENE_BUILDERS[sceneId] ?? placeholderBuilder(sceneId);
}

export function isScenePorted(sceneId: string): boolean {
  return Boolean(SCENE_BUILDERS[sceneId]);
}
